import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, ChevronRight, Loader2, Network, Pencil, Plus, UserX, Users } from 'lucide-react';
import { Button } from '@components/Button';
import { ConfirmDialog, Modal } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ActionButton } from '@components/Table';
import { Avatar } from '@components/activity/ActivityBits';
import { useProfile } from '@contexts/ProfileContext';
import teamService from '@services/teamService';
import type { TeamMember, TeamMemberFormData, TeamMemberNode, TeamPosition } from '@/types/team';
import { POSITION_LABELS } from '@/types/team';

const labelCls = 'text-label block mb-1';

const TreeNode: React.FC<{ node: TeamMemberNode; depth: number; onEdit: (m: TeamMember) => void }> = ({
  node,
  depth,
  onEdit,
}) => {
  const [open, setOpen] = useState(true);
  const hasChildren = node.subordinates?.length > 0;
  return (
    <li>
      <div
        className={clsx('flex items-center gap-2 py-1.5 pr-2 rounded-lg hover:bg-slate-50', !node.is_active && 'opacity-50')}
        style={{ paddingLeft: depth * 20 }}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={clsx('w-5 h-5 flex items-center justify-center text-slate-400', !hasChildren && 'invisible')}
          aria-label={open ? 'Recolher' : 'Expandir'}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <Avatar name={node.name} />
        <button type="button" onClick={() => onEdit(node)} className="text-sm text-slate-800 hover:text-emerald-700">
          {node.name}
        </button>
        <span className={node.position === 'manager' ? 'badge-success' : 'badge-neutral'}>{POSITION_LABELS[node.position]}</span>
        {hasChildren && <span className="text-xs text-slate-400">{node.subordinates.length} subordinado(s)</span>}
      </div>
      {hasChildren && open && (
        <ul>
          {node.subordinates.map((child) => (
            <TreeNode key={child.id} node={child} depth={depth + 1} onEdit={onEdit} />
          ))}
        </ul>
      )}
    </li>
  );
};

const MemberFormModal: React.FC<{
  member: TeamMember | null;
  members: TeamMember[];
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}> = ({ member, members, onClose, onSaved, onError }) => {
  const [form, setForm] = useState({
    name: member?.name ?? '',
    email: member?.email ?? '',
    position: (member?.position ?? 'analyst') as TeamPosition,
    manager_member_id: member?.manager_member_id ? String(member.manager_member_id) : '',
    is_active: member?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSubmit = form.name.trim().length > 0 && emailValid && !saving;

  const handleSave = async () => {
    if (!canSubmit) return;
    const payload: TeamMemberFormData = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      position: form.position,
      manager_member_id: form.manager_member_id ? Number(form.manager_member_id) : null,
      ...(member ? { is_active: form.is_active } : {}),
    };
    setSaving(true);
    try {
      if (member) await teamService.update(member.id, payload);
      else await teamService.create(payload);
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const managerOptions = members.filter((m) => m.is_active && m.id !== member?.id);

  return (
    <Modal isOpen onClose={onClose} size="md" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-h3">{member ? 'Editar membro' : 'Novo membro'}</h2>
      </div>
      <div className="px-6 py-4 space-y-4">
        <div>
          <label className={labelCls}>
            Nome <span className="text-red-500">*</span>
          </label>
          <input className="input-field" maxLength={200} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>
            E-mail corporativo <span className="text-red-500">*</span>
          </label>
          <input
            className="input-field"
            type="email"
            maxLength={200}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="nome@viverorganizer.com.br"
          />
          <p className="text-small mt-1">Precisa ser o mesmo e-mail do login Microsoft — é por ele que o usuário é reconhecido.</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Cargo</label>
            <select
              className="select-field"
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value as TeamPosition })}
            >
              <option value="analyst">{POSITION_LABELS.analyst}</option>
              <option value="manager">{POSITION_LABELS.manager}</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Gestor</label>
            <select
              className="select-field"
              value={form.manager_member_id}
              onChange={(e) => setForm({ ...form, manager_member_id: e.target.value })}
            >
              <option value="">Nenhum (topo)</option>
              {managerOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {member && (
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox-field"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Ativo
          </label>
        )}
        <p className="text-small">
          Quem tem subordinados passa a ver e delegar as atividades deles (o "gestor" vem da hierarquia, não do cargo).
        </p>
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave} loading={saving} disabled={!canSubmit}>
          Salvar
        </Button>
      </div>
    </Modal>
  );
};

/** Administração da equipe: hierarquia (árvore) + cadastro de analistas e gestores. */
export const TeamAdminView: React.FC = () => {
  const { reload: reloadProfile } = useProfile();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [tree, setTree] = useState<TeamMemberNode[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<TeamMember | null | undefined>(undefined);
  const [inactivating, setInactivating] = useState<TeamMember | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, t] = await Promise.all([teamService.getAll({ includeInactive: true }), teamService.getTree()]);
      setMembers(list);
      setTree(t);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const afterChange = () => {
    load();
    reloadProfile();
  };

  const handleInactivate = async () => {
    if (!inactivating) return;
    setBusy(true);
    try {
      await teamService.inactivate(inactivating.id);
      setInactivating(null);
      afterChange();
    } catch (err) {
      setInactivating(null);
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const term = search.trim().toLowerCase();
  const visible = members
    .filter((m) => includeInactive || m.is_active)
    .filter((m) => !term || m.name.toLowerCase().includes(term) || m.email.toLowerCase().includes(term))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Users size={20} className="text-slate-500" /> Equipe
        </h1>
        <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setEditing(null)}>
          Novo membro
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="card p-4 xl:col-span-1">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
            <Network size={16} className="text-slate-500" /> Hierarquia
          </h3>
          {loading && tree.length === 0 ? (
            <div className="text-slate-400 text-sm flex items-center">
              <Loader2 size={16} className="animate-spin mr-2" /> Carregando…
            </div>
          ) : tree.length === 0 ? (
            <p className="text-sm text-slate-400">Nenhum membro cadastrado.</p>
          ) : (
            <ul>
              {tree.map((n) => (
                <TreeNode key={n.id} node={n} depth={0} onEdit={setEditing} />
              ))}
            </ul>
          )}
        </div>

        <div className="xl:col-span-2 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <input
              className="input-field max-w-xs"
              placeholder="Buscar por nome ou e-mail…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox-field"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              Mostrar inativos
            </label>
          </div>
          <div className="table-container bg-white">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th>Nome</th>
                    <th>Cargo</th>
                    <th>Gestor</th>
                    <th>Situação</th>
                    <th className="w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && visible.length === 0 && (
                    <tr>
                      <td colSpan={5} className="table-cell text-center text-slate-500 py-8">
                        Nenhum membro encontrado.
                      </td>
                    </tr>
                  )}
                  {visible.map((m) => (
                    <tr key={m.id} className={clsx('table-row', !m.is_active && 'opacity-60')}>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <Avatar name={m.name} />
                          <div>
                            <p className="font-medium text-slate-800">{m.name}</p>
                            <p className="text-small">{m.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">{POSITION_LABELS[m.position]}</td>
                      <td className="table-cell">{m.manager_name ?? <span className="text-slate-400">—</span>}</td>
                      <td className="table-cell">
                        {m.is_active ? <span className="badge-success">Ativo</span> : <span className="badge-neutral">Inativo</span>}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center justify-end gap-1">
                          <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setEditing(m)} />
                          {m.is_active && (
                            <ActionButton
                              icon={<UserX size={16} />}
                              variant="delete"
                              tooltip="Inativar"
                              onClick={() => setInactivating(m)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {editing !== undefined && (
        <MemberFormModal
          member={editing}
          members={members}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            afterChange();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!inactivating}
        onClose={() => setInactivating(null)}
        onConfirm={handleInactivate}
        title="Inativar membro"
        message={`Inativar ${inactivating?.name ?? ''}? Um gestor com subordinados ativos não pode ser inativado — mova os subordinados antes.`}
        confirmText="Inativar"
        severity="danger"
        loading={busy}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
